import * as React from "react";
import { formInitialize, formUninitialize, useCpFormSubmit } from "../../gvc/testNewForm/utils";
import { v4 as uuidv4 } from "uuid";
import { homeLink, parentLink, request, resourceLink } from "../../../services/cpln";
import { useBlocker, useNavigate } from "react-router-dom";
import { sentryLog } from "../../../tools/sentry";
import { notification } from "antd";
import { CpFormContext } from "../../../components/form/CpFormContext";
import { CpFormValidator } from "../../gvc/testNewForm/CpFormValidator";
import { CpFormPrompt } from "../../../components/antd/form/CpFormPrompt";
import { getValidator } from "./validator";
import { CpPreviewModal } from "../../workload/draft/previewModal";
import { CpDebugModal } from "../../workload/draft/debugModal";
import { getDefaultIdentityInput, inputToIdentity } from "../utils";
import { IdentityInput } from "../../../schema/types/identity/input";
import { IdentityCreateContent } from "./content";
import { useAppDispatch } from "../../../store/hooks";
import { setRedirect } from "../../../store/contextSlice";
import { identityApi } from "../../../store/api/identityApi";
import { store } from "../../../store";
import { parseLink } from "../../../utils/linkParser/linkParser";

interface Props {}
export const IdentityCreateForm: React.FC<Props> = ({}) => {
  const dispatch = useAppDispatch();

  const formIdRef = React.useRef(uuidv4());
  const formId = formIdRef.current;

  // form init
  const [isInitialized, setIsInitialized] = React.useState<boolean>(false);
  React.useEffect(() => {
    const input = getDefaultIdentityInput();
    formInitialize(formId, input);
    setIsInitialized(true);
    return () => {
      formUninitialize(formId);
    };
  }, []);

  // prompt
  const ignoreBlockerRef = React.useRef(false);
  const nextLocationRef = React.useRef("/");
  const blocker = useBlocker(({ currentLocation, nextLocation, historyAction }) => {
    nextLocationRef.current = nextLocation.pathname;

    const res =
      !ignoreBlockerRef.current &&
      currentLocation.pathname.includes(`/identity/-create`) &&
      !nextLocation.pathname.includes(`/identity/-create`);
    if (res) {
      sentryLog("blockedNavigation", {
        "data.ignoreBlockerRef": ignoreBlockerRef.current,
        "pathname.next": nextLocation.pathname,
        "pathname.current": currentLocation.pathname,
        "pathname.check": "-create",
        res: String(res),
      });
    }

    return res;
  });

  // validation/submit
  const validatorRef = React.useRef(getValidator(formId));
  const validator = validatorRef.current;
  const submit = useCpFormSubmit({ id: formId, validationFn: validator, onSubmit: handleSubmit });

  async function handleSubmit(input: IdentityInput) {
    try {
      const org = store.getState().context.org;
      const gvc = parseLink(input.gvc.link, { useInputCtx: true }).name;
      const ctx = { org: org, gvc: gvc };
      let body = inputToIdentity(input);
      const method = "post";
      const url = parentLink("identity", ctx);

      await request({
        method: method,
        url: url,
        body: body,
      });

      const itemLink = resourceLink("identity", body.name, ctx);
      dispatch(identityApi.util.invalidateTags([{ type: "Identity", id: itemLink }]));
      ignoreBlockerRef.current = true;
      notification.success({
        message: "Success",
        description: "Identity is created",
      });
      dispatch(setRedirect(`/console${itemLink}`));
    } catch (e) {
      let errorMessage = e?.response?.data?.message;
      if (!errorMessage) errorMessage = e.message;
      notification.warning({
        message: "Failed",
        description: errorMessage,
      });
    }
  }

  if (!isInitialized) {
    return null;
  }

  return (
    <CpFormContext.Provider value={{ id: formId, submit: submit }}>
      <IdentityCreateContent ignoreBlockerRef={ignoreBlockerRef} />
      <CpFormValidator validationFn={validator} />
      <CpFormPrompt blocker={blocker} nextLocationRef={nextLocationRef} />
      <CpPreviewModal inputToItemFn={inputToIdentity} kind="identity" namePath="name" title="Identity" />
      <CpDebugModal inputToItemFn={inputToIdentity} kind="identity" namePath="name" title="Identity" />
    </CpFormContext.Provider>
  );
};
